import html2canvas from 'html2canvas'

class ScreenshotService {
  constructor() {
    this.maxRetries = 3
    this.captureTimeout = 30000
    this.preloadDelay = 500
    this.scrollDelay = 200
  }

  async captureScreenshot() {
    // Store original state
    const originalState = this.saveOriginalState()
    
    try {
      // Clean up v-dialogs and hide UI elements first
      this.cleanupVDialogs()
      this.hideUIElements()
      
      // Try multiple capture strategies with full page priority
      const strategies = [
        () => this.captureWithScrollMethod(),  // Try full page first
        () => this.captureWithViewportMethod(),
        () => this.captureSimpleViewport()
      ]
      
      for (let i = 0; i < strategies.length; i++) {
        try {
          const result = await strategies[i]()
          
          if (result && this.validateScreenshot(result)) {
            return {
              type: 'screenshot',
              name: 'page-screenshot.jpg',
              data: result,
              size: Math.round(result.length / 1024),
              strategy: i + 1
            }
          }
        } catch (error) {
          console.warn(`Screenshot strategy ${i + 1} failed:`, error.message)
          continue
        }
      }
      
      return null
      
    } finally {
      this.restoreOriginalState(originalState)
    }
  }

  saveOriginalState() {
    return {
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      bodyOverflow: document.body.style.overflow,
      htmlOverflow: document.documentElement.style.overflow,
      uiElements: this.getUIElementsState(),
      vDialogs: this.getVDialogElements()
    }
  }

  restoreOriginalState(state) {
    try {
      window.scrollTo(state.scrollX, state.scrollY)
      document.body.style.overflow = state.bodyOverflow
      document.documentElement.style.overflow = state.htmlOverflow
      this.restoreUIElements(state.uiElements)
      this.restoreVDialogs(state.vDialogs)
    } catch (error) {
      console.warn('Error restoring original state:', error)
    }
  }

  getUIElementsState() {
    const selectors = ['.bug-modal-overlay', '.bug-report-btn', '.success-message']
    const state = {}
    
    selectors.forEach(selector => {
      const element = document.querySelector(selector)
      if (element) {
        state[selector] = {
          display: element.style.display,
          visibility: element.style.visibility
        }
      }
    })
    
    return state
  }

  restoreUIElements(state) {
    Object.keys(state).forEach(selector => {
      const element = document.querySelector(selector)
      if (element) {
        element.style.display = state[selector].display
        element.style.visibility = state[selector].visibility
      }
    })
  }

  // === V-DIALOG SPECIFIC METHODS ===

  /**
   * Get all v-dialog related elements and their state
   */
  getVDialogElements() {
    const vDialogState = []
    const selectors = [
      '.v-dialog',
      '.v-overlay',
      '.v-overlay__content',
      '.v-overlay__scrim'
    ]

    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector)
      elements.forEach(element => {
        // Skip bug reporter's own modals
        if (element.closest('.bug-reporter') || element.hasAttribute('data-bug-reporter')) {
          return
        }

        const isVisible = this.isElementVisible(element)
        
        vDialogState.push({
          element: element,
          selector: selector,
          originalDisplay: element.style.display,
          originalVisibility: element.style.visibility,
          originalOpacity: element.style.opacity,
          originalZIndex: element.style.zIndex,
          wasVisible: isVisible,
          shouldPreserveForScreenshot: isVisible // Mark visible dialogs for preservation
        })
      })
    })

    return vDialogState
  }

  /**
   * Check if element is currently visible
   */
  isElementVisible(element) {
    const style = window.getComputedStyle(element)
    return style.display !== 'none' && 
           style.visibility !== 'hidden' && 
           style.opacity !== '0' &&
           element.offsetWidth > 0 && 
           element.offsetHeight > 0
  }

  /**
   * Clean up and hide all v-dialog elements
   * Preserves visible dialogs that should be included in screenshot
   */
  cleanupVDialogs() {
    // Get current state to identify which dialogs are visible
    const currentDialogState = this.getVDialogElements()
    
    const selectors = [
      '.v-dialog:not([data-bug-reporter]):not(.bug-reporter *)',
      '.v-overlay:not([data-bug-reporter]):not(.bug-reporter *)',
      '.v-overlay__content:not([data-bug-reporter]):not(.bug-reporter *)',
      '.v-overlay__scrim:not([data-bug-reporter]):not(.bug-reporter *)'
    ]

    let hiddenCount = 0
    let preservedCount = 0
    
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector)
      elements.forEach(element => {
        // Check if this element should be preserved for screenshot
        const dialogState = currentDialogState.find(state => state.element === element)
        const shouldPreserve = dialogState && dialogState.shouldPreserveForScreenshot
        
        if (shouldPreserve) {
          // Keep visible dialogs visible but adjust z-index to be below bug reporter
          element.style.zIndex = '10000' // Below bug reporter's 15000
          preservedCount++
          console.log('📌 Preserving visible dialog for screenshot:', element.className)
        } else {
          // Hide non-visible dialogs
          element.style.display = 'none'
          element.style.visibility = 'hidden'
          element.style.opacity = '0'
          element.style.zIndex = '-9999'
          hiddenCount++
        }
      })
    })

    console.log(`📸 Hidden ${hiddenCount} v-dialog elements, preserved ${preservedCount} visible dialogs for screenshot`)

    // Only remove truly orphaned elements that are safe to remove
    this.removeOrphanedDialogs()
  }

  /**
   * Remove orphaned dialog elements that may be stuck in DOM
   * Only removes elements that are confirmed orphans and safe to remove
   */
  removeOrphanedDialogs() {
    const selectors = [
      '.v-overlay:not(.bug-reporter *)',
      '.v-dialog:not(.bug-reporter *)'
    ]

    let removedCount = 0
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector)
      elements.forEach(element => {
        // Only remove elements that are clearly orphaned and safe to remove
        const computedStyle = window.getComputedStyle(element)
        const hasNoContent = element.children.length === 0
        const isHidden = computedStyle.display === 'none' || computedStyle.visibility === 'hidden'
        const hasNoParent = !element.closest('[data-app]') && !element.closest('.v-application')
        const hasNoVueInstance = !element.__vue__ && !element._vnode
        
        // Only remove if it meets multiple criteria for being orphaned AND has no Vue instance
        if (isHidden && hasNoContent && hasNoParent && hasNoVueInstance) {
          // Add a small delay to ensure Vue isn't using this element
          setTimeout(() => {
            if (element.parentNode && !element.closest('.v-application')) {
              element.remove()
              removedCount++
            }
          }, 100)
        }
        // For elements that seem attached to Vue, just hide them safely
        else if (!element.closest('[data-app]') && !element.closest('.v-application')) {
          element.style.display = 'none'
          element.style.visibility = 'hidden'
        }
      })
    })

    if (removedCount > 0) {
      console.log(`🧹 Scheduled removal of ${removedCount} confirmed orphaned dialog elements`)
    }
  }

  /**
   * Restore v-dialog elements to their original state
   */
  restoreVDialogs(vDialogState) {
    vDialogState.forEach(dialogData => {
      if (dialogData.element && dialogData.element.parentNode) {
        try {
          // Restore original styles properly
          if (dialogData.originalDisplay) {
            dialogData.element.style.display = dialogData.originalDisplay
          } else {
            dialogData.element.style.removeProperty('display')
          }
          
          if (dialogData.originalVisibility) {
            dialogData.element.style.visibility = dialogData.originalVisibility
          } else {
            dialogData.element.style.removeProperty('visibility')
          }
          
          if (dialogData.originalOpacity) {
            dialogData.element.style.opacity = dialogData.originalOpacity
          } else {
            dialogData.element.style.removeProperty('opacity')
          }
          
          if (dialogData.originalZIndex) {
            dialogData.element.style.zIndex = dialogData.originalZIndex
          } else {
            dialogData.element.style.removeProperty('z-index')
          }
          
          // Force a style recalculation to ensure Vuetify can work with the element
          dialogData.element.offsetHeight
          
        } catch (error) {
          console.warn('Error restoring v-dialog:', error)
        }
      }
    })

    console.log(`🔄 Restored ${vDialogState.length} v-dialog elements`)
    
    // Trigger a small delay to allow Vue to reinitialize dialog functionality
    setTimeout(() => {
      // Dispatch a custom event to let Vue know dialogs are restored
      window.dispatchEvent(new CustomEvent('v-dialogs-restored'))
    }, 50)
  }

  // Strategy 1: Viewport-based capture with content preloading
  async captureWithViewportMethod() {
    // Scroll to top
    window.scrollTo(0, 0)
    await this.wait(this.preloadDelay)
    
    // Get page dimensions
    const dimensions = this.getPageDimensions()
    
    // Prepare the page for capture
    this.preparePage()
    
    const canvas = await this.createCanvasWithTimeout({
      allowTaint: true,
      useCORS: true,
      scale: this.calculateOptimalScale(dimensions),
      width: dimensions.viewportWidth,
      height: dimensions.viewportHeight,
      scrollX: 0,
      scrollY: 0,
      backgroundColor: '#ffffff',
      logging: false,
      removeContainer: true,
      foreignObjectRendering: false,
      imageTimeout: 15000,
      ignoreElements: (el) => this.shouldIgnoreElement(el),
      onclone: (clonedDoc) => this.optimizeClonedDocument(clonedDoc)
    })
    
    return this.processCanvas(canvas)
  }

  // Strategy 2: Scroll-based capture for full page
  async captureWithScrollMethod() {
    const dimensions = this.getPageDimensions()
    
    // Always try full page capture, even for shorter pages
    
    // Scroll through page to load content
    await this.preloadPageContent(dimensions)
    
    // Return to top and wait
    window.scrollTo(0, 0)
    await this.wait(this.preloadDelay)
    
    // Prepare page for full capture
    const pageStyles = this.preparePage()
    
    // Calculate safe dimensions to avoid memory issues
    const maxHeight = Math.min(dimensions.fullHeight, 10000)
    const scale = this.calculateOptimalScale({ 
      fullWidth: dimensions.fullWidth, 
      fullHeight: maxHeight 
    })
    
    const canvas = await this.createCanvasWithTimeout({
      allowTaint: true,
      useCORS: true,
      scale: scale,
      width: dimensions.fullWidth,
      height: maxHeight,
      scrollX: 0,
      scrollY: 0,
      x: 0,
      y: 0,
      backgroundColor: '#ffffff',
      logging: false,
      removeContainer: true,
      foreignObjectRendering: false,
      imageTimeout: 25000,
      windowWidth: dimensions.fullWidth,
      windowHeight: maxHeight,
      ignoreElements: (el) => this.shouldIgnoreElement(el),
      onclone: (clonedDoc) => this.optimizeClonedDocument(clonedDoc, dimensions, maxHeight)
    })
    
    return this.processCanvas(canvas)
  }

  // Strategy 3: Simple viewport capture (fallback)
  async captureSimpleViewport() {
    window.scrollTo(0, 0)
    await this.wait(300)
    
    const canvas = await this.createCanvasWithTimeout({
      allowTaint: true,
      useCORS: true,
      scale: 0.8,
      backgroundColor: '#ffffff',
      logging: false,
      ignoreElements: (el) => this.shouldIgnoreElement(el)
    })
    
    return this.processCanvas(canvas)
  }

  getPageDimensions() {
    const body = document.body
    const html = document.documentElement
    
    return {
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      fullWidth: Math.max(
        body.scrollWidth, body.offsetWidth,
        html.clientWidth, html.scrollWidth, html.offsetWidth
      ),
      fullHeight: Math.max(
        body.scrollHeight, body.offsetHeight,
        html.clientHeight, html.scrollHeight, html.offsetHeight
      )
    }
  }

  calculateOptimalScale(dimensions) {
    const maxWidth = 1600
    const maxHeight = 6000
    const maxPixels = 4000000 // 4MP to avoid memory issues
    
    const width = dimensions.fullWidth || dimensions.viewportWidth
    const height = dimensions.fullHeight || dimensions.viewportHeight
    
    // Calculate scale based on dimensions
    const widthScale = width > maxWidth ? maxWidth / width : 1
    const heightScale = height > maxHeight ? maxHeight / height : 1
    
    // Calculate scale based on total pixels
    const totalPixels = width * height
    const pixelScale = totalPixels > maxPixels ? Math.sqrt(maxPixels / totalPixels) : 1
    
    // Use the most restrictive scale
    const scale = Math.min(widthScale, heightScale, pixelScale, 1)
    
    // Ensure minimum quality
    return Math.max(scale, 0.3)
  }

  preparePage() {
    // Fix body and html styles for better capture
    const originalBodyOverflow = document.body.style.overflow
    const originalHtmlOverflow = document.documentElement.style.overflow
    
    document.body.style.overflow = 'visible'
    document.documentElement.style.overflow = 'visible'
    
    // Force a reflow
    document.body.offsetHeight
    
    return { originalBodyOverflow, originalHtmlOverflow }
  }

  async preloadPageContent(dimensions) {
    const scrollStep = Math.max(dimensions.viewportHeight * 0.8, 400)
    const totalHeight = dimensions.fullHeight
    
    // Scroll through the page to trigger lazy loading
    for (let y = 0; y < totalHeight; y += scrollStep) {
      window.scrollTo(0, Math.min(y, totalHeight))
      await this.wait(this.scrollDelay)
      
      // Trigger events to ensure content loads
      this.triggerLoadEvents()
    }
    
    // Final scroll to bottom and back to top
    window.scrollTo(0, totalHeight)
    await this.wait(this.preloadDelay)
  }

  triggerLoadEvents() {
    try {
      window.dispatchEvent(new Event('scroll'))
      window.dispatchEvent(new Event('resize'))
      
      // Trigger intersection observer updates
      const images = document.querySelectorAll('img[data-src], img[loading="lazy"]')
      images.forEach(img => {
        if (img.getBoundingClientRect().top < window.innerHeight * 2) {
          img.src = img.dataset.src || img.src
        }
      })
    } catch (error) {
      // Ignore errors in event triggering
    }
  }

  optimizeClonedDocument(clonedDoc, dimensions = null, targetHeight = null) {
    try {
      // Remove problematic elements including v-dialogs
      this.removeProblematicElements(clonedDoc)
      
      // Fix positioning issues for full page
      this.fixPositioning(clonedDoc, true, dimensions, targetHeight)
      
      // Optimize styles
      this.optimizeStyles(clonedDoc, targetHeight)
      
      // Force content visibility
      this.forceContentVisibility(clonedDoc)
      
      // Ensure full document is visible
      if (dimensions && targetHeight) {
        this.ensureFullDocumentVisible(clonedDoc, dimensions, targetHeight)
      }
      
    } catch (error) {
      console.warn('Error optimizing cloned document:', error)
    }
  }

  removeProblematicElements(clonedDoc) {
    const problematicSelectors = [
      '.bug-modal-overlay',
      '.bug-report-btn',
      '.success-message',
      // Remove only hidden v-dialogs, preserve visible ones for screenshot
      '.v-dialog:not([data-bug-reporter])[style*="display: none"]',
      '.v-dialog:not([data-bug-reporter])[style*="visibility: hidden"]',
      '.v-overlay:not([data-bug-reporter])[style*="display: none"]',
      '.v-overlay:not([data-bug-reporter])[style*="visibility: hidden"]',
      '.v-overlay__content:not([data-bug-reporter])[style*="display: none"]',
      '.v-overlay__scrim:not([data-bug-reporter])[style*="display: none"]',
      'script',
      'noscript',
      'iframe[src*="google"]',
      '[data-html2canvas-ignore]'
    ]
    
    problematicSelectors.forEach(selector => {
      try {
        const elements = clonedDoc.querySelectorAll(selector)
        elements.forEach(el => {
          // Double-check it's not our bug reporter modal and it's actually hidden
          if (!el.closest('.bug-reporter') && !el.hasAttribute('data-bug-reporter')) {
            el.remove()
          }
        })
      } catch (e) {
        // Ignore removal errors
      }
    })

    // Also remove hidden v-overlays that might not match the style selectors
    const hiddenOverlays = clonedDoc.querySelectorAll('.v-overlay:not([data-bug-reporter])')
    hiddenOverlays.forEach(overlay => {
      const style = window.getComputedStyle(overlay)
      if (style.display === 'none' || style.visibility === 'hidden') {
        overlay.remove()
      }
    })
  }

  fixPositioning(clonedDoc, isFullPage, dimensions = null, targetHeight = null) {
    const allElements = clonedDoc.querySelectorAll('*')
    
    allElements.forEach(el => {
      try {
        const style = el.style
        
        // Get computed styles from original element if possible
        let computedStyle = null
        try {
          const originalEl = document.querySelector(this.getElementSelector(el))
          if (originalEl) {
            computedStyle = window.getComputedStyle(originalEl)
          }
        } catch (e) {
          // Ignore if we can't find original element
        }
        
        // Convert problematic positioning for full page capture
        if (computedStyle && ['fixed', 'sticky'].includes(computedStyle.position)) {
          style.position = 'absolute'
          
          if (isFullPage && dimensions) {
            // Calculate absolute position relative to document
            try {
              const originalEl = document.querySelector(this.getElementSelector(el))
              if (originalEl) {
                const rect = originalEl.getBoundingClientRect()
                style.top = (rect.top + window.scrollY) + 'px'
                style.left = (rect.left + window.scrollX) + 'px'
                style.right = 'auto'
                style.bottom = 'auto'
              }
            } catch (e) {
              // If positioning fails, just set to static
              style.position = 'static'
            }
          }
        }
        
        // Remove problematic transforms
        if (style.transform && (
          style.transform.includes('translate') ||
          style.transform.includes('matrix') ||
          style.transform.includes('scale')
        )) {
          style.transform = 'none'
        }
        
        // Fix z-index issues
        if (style.zIndex && parseInt(style.zIndex) > 999) {
          style.zIndex = '1'
        }
        
        // Remove viewport units that might cause issues
        if (style.height && (style.height.includes('vh') || style.height.includes('vw'))) {
          if (targetHeight) {
            const vhValue = parseFloat(style.height)
            if (style.height.includes('vh')) {
              style.height = (targetHeight * vhValue / 100) + 'px'
            }
          }
        }
        
      } catch (e) {
        // Ignore style errors
      }
    })
  }

  getElementSelector(element) {
    // Simple selector generation for finding original element
    try {
      if (element.id) {
        return `#${element.id}`
      }
      if (element.className && typeof element.className === 'string') {
        const classes = element.className.split(' ').filter(c => c.length > 0)
        if (classes.length > 0) {
          return `.${classes[0]}`
        }
      }
      return element.tagName.toLowerCase()
    } catch (e) {
      return element.tagName.toLowerCase()
    }
  }

  optimizeStyles(clonedDoc, targetHeight = null) {
    try {
      // Set document background and ensure full height
      if (clonedDoc.body) {
        clonedDoc.body.style.backgroundColor = '#ffffff'
        clonedDoc.body.style.overflow = 'visible'
        clonedDoc.body.style.position = 'relative'
        
        if (targetHeight) {
          clonedDoc.body.style.minHeight = targetHeight + 'px'
          clonedDoc.body.style.height = 'auto'
        }
      }
      
      if (clonedDoc.documentElement) {
        clonedDoc.documentElement.style.backgroundColor = '#ffffff'
        clonedDoc.documentElement.style.overflow = 'visible'
        clonedDoc.documentElement.style.position = 'relative'
        
        if (targetHeight) {
          clonedDoc.documentElement.style.minHeight = targetHeight + 'px'
          clonedDoc.documentElement.style.height = 'auto'
        }
      }
      
      // Fix any containers that might cut off content
      const containers = clonedDoc.querySelectorAll('div, section, main, article')
      containers.forEach(container => {
        const style = container.style
        if (style.height && !style.height.includes('auto')) {
          style.height = 'auto'
        }
        if (style.maxHeight) {
          style.maxHeight = 'none'
        }
        if (style.overflow === 'hidden') {
          style.overflow = 'visible'
        }
      })
      
    } catch (error) {
      console.warn('Error optimizing styles:', error)
    }
  }

  ensureFullDocumentVisible(clonedDoc, dimensions, targetHeight) {
    try {
      // Force all content to be visible within the target height
      const allElements = clonedDoc.querySelectorAll('*')
      
      allElements.forEach(el => {
        const style = el.style
        
        // Remove any clipping
        if (style.clip && style.clip !== 'auto') {
          style.clip = 'auto'
        }
        
        if (style.clipPath && style.clipPath !== 'none') {
          style.clipPath = 'none'
        }
        
        // Ensure elements don't go beyond document bounds
        if (style.position === 'absolute' || style.position === 'fixed') {
          const top = parseInt(style.top) || 0
          const left = parseInt(style.left) || 0
          
          if (top > targetHeight) {
            style.top = (targetHeight - 100) + 'px'
          }
          
          if (left > dimensions.fullWidth) {
            style.left = (dimensions.fullWidth - 100) + 'px'
          }
        }
      })
      
    } catch (error) {
      console.warn('Error ensuring document visibility:', error)
    }
  }

  forceContentVisibility(clonedDoc) {
    const hiddenElements = clonedDoc.querySelectorAll('[style*="display: none"], [style*="visibility: hidden"]')
    
    hiddenElements.forEach(el => {
      try {
        const style = el.style
        if (style.display === 'none' && !this.shouldStayHidden(el)) {
          style.display = 'block'
        }
        if (style.visibility === 'hidden') {
          style.visibility = 'visible'
        }
      } catch (e) {
        // Ignore errors
      }
    })
  }

  shouldStayHidden(element) {
    // Keep some elements hidden for good reasons
    const classNames = element.className || ''
    return classNames.includes('bug-') || 
           classNames.includes('hidden') ||
           classNames.includes('v-dialog') ||
           classNames.includes('v-overlay') ||
           element.hasAttribute('data-hidden')
  }

  createCanvasWithTimeout(options) {
    return Promise.race([
      html2canvas(document.body, options),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Screenshot timeout')), this.captureTimeout)
      )
    ])
  }

  shouldIgnoreElement(element) {
    if (!element || !element.classList) return false
    
    const ignoredClasses = [
      'bug-modal-overlay',
      'bug-report-btn', 
      'success-message'
    ]
    
    // Check if element is inside bug reporter
    if (element.closest('.bug-reporter')) {
      return false // Don't ignore bug reporter's own elements
    }
    
    // For v-dialog elements, only ignore if they're hidden
    if (element.classList.contains('v-dialog') || 
        element.classList.contains('v-overlay') ||
        element.classList.contains('v-overlay__content') ||
        element.classList.contains('v-overlay__scrim')) {
      
      const style = window.getComputedStyle(element)
      const isHidden = style.display === 'none' || 
                      style.visibility === 'hidden' || 
                      style.opacity === '0'
      
      return isHidden // Only ignore hidden v-dialog elements
    }
    
    return ignoredClasses.some(className => element.classList.contains(className))
  }

  processCanvas(canvas) {
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      throw new Error('Invalid canvas generated')
    }
    
    // Resize if too large
    const maxSize = { width: 1600, height: 4000 }
    
    if (canvas.width > maxSize.width || canvas.height > maxSize.height) {
      canvas = this.resizeCanvas(canvas, maxSize)
    }
    
    // Convert to JPEG with good quality
    const dataURL = canvas.toDataURL('image/jpeg', 0.85)
    
    if (!this.validateScreenshot(dataURL)) {
      throw new Error('Generated screenshot is invalid')
    }
    
    return dataURL
  }

  resizeCanvas(canvas, maxSize) {
    const ratio = Math.min(
      maxSize.width / canvas.width,
      maxSize.height / canvas.height
    )
    
    const newWidth = Math.floor(canvas.width * ratio)
    const newHeight = Math.floor(canvas.height * ratio)
    
    const resizedCanvas = document.createElement('canvas')
    resizedCanvas.width = newWidth
    resizedCanvas.height = newHeight
    
    const ctx = resizedCanvas.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, newWidth, newHeight)
    
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(canvas, 0, 0, newWidth, newHeight)
    
    return resizedCanvas
  }

  validateScreenshot(dataURL) {
    // Basic validation
    if (!dataURL || !dataURL.startsWith('data:image/')) {
      return false
    }
    
    // Check minimum size (should be at least 10KB)
    if (dataURL.length < 10000) {
      return false
    }
    
    return true
  }

  hideUIElements() {
    const selectors = [
      '.bug-modal-overlay', 
      '.bug-report-btn', 
      '.success-message'
    ]
    
    // For v-dialogs, only hide those that are already hidden (not currently open)
    const vDialogSelectors = [
      '.v-dialog:not([data-bug-reporter]):not(.bug-reporter *)',
      '.v-overlay:not([data-bug-reporter]):not(.bug-reporter *)'
    ]
    
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector)
      elements.forEach(element => {
        element.style.display = 'none'
      })
    })
    
    // For v-dialogs, check visibility before hiding
    vDialogSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector)
      elements.forEach(element => {
        const isCurrentlyVisible = this.isElementVisible(element)
        
        if (!isCurrentlyVisible) {
          // Only hide if it's not currently visible
          element.style.display = 'none'
        } else {
          // Keep visible dialogs but ensure they're below bug reporter
          element.style.zIndex = '10000'
        }
      })
    })
  }

  wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }
}

export default new ScreenshotService()